import { useState, useEffect, useRef } from "react";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import {
  A11y,
  Keyboard,
  Autoplay,
  Pagination,
  Navigation,
} from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);

  const prevRef = useRef(null);
  const nextRef = useRef(null);

  useEffect(() => {
    fetch("/testimonials.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load testimonials");
        return res.json();
      })
      .then((data) => {
        const formatted = data.map((item, index) => ({
          id: index + 1,
          name: item.author,
          role: item.role,
          rating: 5,
          text: item.quote,
        }));
        setTestimonials(formatted);
      })
      .catch((err) => console.error(err));
  }, []);

  return (
    <section className="relative py-10 lg:py-20 bg-gradient-to-b from-white via-blue-50 to-indigo-50 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-300/20 blur-3xl rounded-full" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-indigo-300/20 blur-3xl rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white rounded-full shadow border border-blue-200 mb-6">
            <span className="text-xl">💬</span>
            <span className="text-sm font-bold text-blue-600 uppercase tracking-wider">
              Success Stories
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            What Our Students{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Say About Us
            </span>
          </h2>

          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Join thousands of professionals who transformed their communication skills
          </p>
        </header>

        {/* Swiper wrapper */}
        <div className="relative">
          {/* Desktop Navigation */}
          <button
            ref={prevRef}
            aria-label="Previous testimonial"
            className="hidden lg:flex absolute -left-16 top-1/2 -translate-y-1/2 z-10
                       w-12 h-12 rounded-full bg-white shadow-lg items-center justify-center
                       text-gray-700 hover:text-blue-600 transition"
          >
            <HiChevronLeft className="w-6 h-6" />
          </button>

          <button
            ref={nextRef}
            aria-label="Next testimonial"
            className="hidden lg:flex absolute -right-16 top-1/2 -translate-y-1/2 z-10
                       w-12 h-12 rounded-full bg-white shadow-lg items-center justify-center
                       text-gray-700 hover:text-blue-600 transition"
          >
            <HiChevronRight className="w-6 h-6" />
          </button>

          <Swiper
            modules={[A11y, Keyboard, Autoplay, Pagination, Navigation]}
            slidesPerView="auto"
            spaceBetween={24}
            loop
            grabCursor
            keyboard={{ enabled: true }}
            autoplay={{
              delay: 3500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{ clickable: true }}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }}
            breakpoints={{
              320: { centeredSlides: true },
              768: { centeredSlides: false },
            }}
            className="!pb-16"
          >
            {testimonials.map((t) => (
              <SwiperSlide key={t.id} className="!w-auto">
                <article className="w-[320px] sm:w-[360px] lg:w-[420px] h-full group">
                  <div className="relative h-[360px] bg-white rounded-3xl p-8 shadow-xl border border-blue-100 flex flex-col overflow-hidden">

                    {/* Rating */}
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <span key={i} className="text-xl text-yellow-400">⭐</span>
                      ))}
                    </div>

                    {/* Quote */}
                    <div className="relative flex-1">
                      {/* Mobile / Tablet: scrollable text */}
                      <p
                        className="
      text-gray-700 text-lg italic leading-relaxed
      max-h-[150px] overflow-y-auto pr-2
      line-clamp-none
      lg:line-clamp-4 lg:overflow-hidden
    "
                      >
                        “{t.text}”
                      </p>

                      {/* Desktop hover overlay */}
                      <div
                        className="
      absolute inset-0 bg-white/95 backdrop-blur-sm
      opacity-0 pointer-events-none
      group-hover:opacity-100 group-hover:pointer-events-auto
      transition-opacity duration-300
      p-4 overflow-y-auto
      hidden lg:block
    "
                      >
                        <p className="text-gray-800 text-lg italic leading-relaxed">
                          “{t.text}”
                        </p>
                      </div>
                    </div>
                    {/* Author */}
                    <div className="flex items-center gap-4 pt-6 border-t border-gray-100 mt-6">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">
                          {t.name.charAt(0)}
                        </span>
                      </div>

                      <div>
                        <p className="font-bold text-gray-900">{t.name}</p>
                        <p className="text-sm text-gray-600">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </article>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;




// import { useEffect, useState } from "react";
// import {
//   ChatBubbleLeftRightIcon,
//   AcademicCapIcon,
//   BriefcaseIcon,
// } from "@heroicons/react/24/outline";



// const Testimonials = () => {
//   const [testimonials, setTestimonials] = useState([]);

//   useEffect(() => {
//     fetch("/testimonials.json")
//       .then((res) => res.json())
//       .then((data) => setTestimonials(data))
//       .catch((err) => console.error("Error fetching testimonials:", err));
//   }, []);

//   return (
//     <section
//   className="py-10 sm:py-16 bg-white"
//   aria-labelledby="testimonials-heading"
// >
//   <div className="max-w-7xl mx-auto px-4 sm:px-6">

//     <header className="mb-10 sm:mb-12 motion-safe:animate-fade-up motion-reduce:animate-none">
//       <h2
//         id="testimonials-heading"
//         className="text-2xl sm:text-3xl md:text-4xl font-bold text-textPrimary"
//       >
//         What learners say
//       </h2>
//       <p className="mt-2 text-textSecondary max-w-2xl">
//         Real experiences from learners who improved their confidence,
//         communication, and career outcomes with Well Spoken.
//       </p>
//     </header>

//     <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
//       {testimonials.map((item, index) => {
//         const gradients = [
//           "from-red-100 to-red-150",
//           "from-yellow-100 to-green-150",
//           "from-red-100 to-blue-100",
//         ];

//         const RoleIcon =
//           item.role?.toLowerCase().includes("student")
//             ? AcademicCapIcon
//             : BriefcaseIcon;

//         return (
//           <figure
//             key={index}
//             tabIndex={0}
//             className={`
//               group
//               bg-gradient-to-br ${gradients[index % gradients.length]}
//               border border-border rounded-2xl p-6
//               transition-all duration-300 ease-out-soft
//               motion-safe:animate-fade-up
//               motion-reduce:animate-none
//               lg:hover:-translate-y-1
//               lg:hover:shadow-hover
//               lg:hover:scale-[1.01]
//               focus-visible:outline-none
//               focus-visible:ring-2 focus-visible:ring-primary-light
//               will-change-transform
//             `}
//             style={{ animationDelay: `${index * 80}ms` }}
//           >
//             {/* Quote Icon */}
//             <ChatBubbleLeftRightIcon
//               aria-hidden="true"
//               className="
//                 h-6 w-6 text-primary/40 mb-4
//                 transition-transform duration-300 ease-out-soft
//                 group-hover:scale-110
//               "
//             />

//             <blockquote className="text-textSecondary leading-relaxed">
//               <span className="sr-only">Testimonial:</span>
//               “{item.quote}”
//             </blockquote>

//             <figcaption className="mt-6 flex items-center gap-3">
//               {/* Role Icon */}
//               <RoleIcon
//                 aria-hidden="true"
//                 className="
//                   h-5 w-5 text-primary/70
//                   transition-transform duration-300 ease-out-soft
//                   group-hover:scale-110
//                 "
//               />

//               <div>
//                 <p className="font-semibold text-textPrimary">
//                   {item.author}
//                 </p>
//                 <p className="text-sm text-textSecondary">
//                   {item.role}
//                 </p>
//               </div>
//             </figcaption>
//           </figure>
//         );
//       })}
//     </div>

//   </div>
// </section>

//   );
// };

// export default Testimonials;
